import React, { useState, useEffect } from 'react';
import { getQuote, updateQuote, approveQuote } from '../api/client';

function ColorSwatch({ color, label }) {
    if (!color?.code) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: color.hex || '#ccc',
                border: '1px solid #999',
                borderRadius: '4px',
                marginRight: '10px',
                flexShrink: 0
            }} />
            <span><strong>{label}:</strong> {color.code} - {color.name}</span>
        </div>
    );
}

function StatusBadge({ status }) {
    const styles = {
        processing: { bg: '#fef3c7', color: '#92400e', text: 'Processing' },
        pending: { bg: '#dbeafe', color: '#1e40af', text: 'Pending Review' },
        needs_manual_review: { bg: '#fee2e2', color: '#991b1b', text: 'Needs Review' },
        approved: { bg: '#d1fae5', color: '#065f46', text: 'Approved' },
        sent: { bg: '#d1fae5', color: '#065f46', text: 'Sent' },
        declined: { bg: '#f3f4f6', color: '#6b7280', text: 'Declined' }
    };
    const s = styles[status] || styles.pending;
    return (
        <span style={{
            backgroundColor: s.bg,
            color: s.color,
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            marginLeft: '10px'
        }}>{s.text}</span>
    );
}

function calculateExteriorSqft(m) {
    if (!m) return 0;
    const front = (m.frontWall?.length || 0) * (m.frontWall?.height || 0);
    const left = (m.leftWall?.length || 0) * (m.leftWall?.height || 0);
    const right = (m.rightWall?.length || 0) * (m.rightWall?.height || 0);
    const back = (m.backWall?.length || 0) * (m.backWall?.height || 0);
    return front + left + right + back;
}

const PAINT_TIER_LABELS = {
    a100: 'A-100 (Budget)',
    superpaint: 'SuperPaint (Standard)',
    latitude: 'Latitude (Premium)',
    emerald: 'Emerald Rain Refresh (Ultimate)'
};

function formatPaintTier(value) {
    return PAINT_TIER_LABELS[value] || value || 'Unknown';
}

function formatColorComplexity(value) {
    if (value === 'light') return 'Light color / primer';
    if (value === 'complex') return 'Multi-color (4+ colors)';
    return 'Standard (1-3 colors)';
}

export default function QuoteDetail({ quote: initialQuote, onClose }) {
    const [quote, setQuote] = useState(initialQuote);
    const [loading, setLoading] = useState(true);
    const [selectedPaint, setSelectedPaint] = useState('superpaint');
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [ownerAdjustment, setOwnerAdjustment] = useState(0);
    const [ownerAdjustmentInput, setOwnerAdjustmentInput] = useState('0');
    const [ownerNotes, setOwnerNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadFullQuote();
    }, [initialQuote.id]);

    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    const loadFullQuote = async () => {
        try {
            const result = await getQuote(initialQuote.id);
            setQuote(result.quote);
            const parsedColors = typeof result.quote.colors === 'string'
                ? JSON.parse(result.quote.colors)
                : (result.quote.colors || {});
            if (parsedColors.paintQualityPreference && selectedPaint === 'superpaint') {
                setSelectedPaint(parsedColors.paintQualityPreference);
            }
            // Load existing adjustments if present
            if (result.quote.ownerAdjustments) {
                const adj = typeof result.quote.ownerAdjustments === 'string'
                    ? JSON.parse(result.quote.ownerAdjustments)
                    : result.quote.ownerAdjustments;
                const amount = adj.amount || 0;
                setOwnerAdjustment(amount);
                setOwnerAdjustmentInput(String(amount));
            } else {
                setOwnerAdjustment(0);
                setOwnerAdjustmentInput('0');
            }
            if (result.quote.ownerNotes) {
                setOwnerNotes(result.quote.ownerNotes);
            } else {
                setOwnerNotes('');
            }
        } catch (error) {
            console.error('Failed to load quote details:', error);
        } finally {
            setLoading(false);
        }
    };

    const parseAdjustmentInput = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed === '' || trimmed === '-' || trimmed === '+') return null;
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const handleSaveAdjustments = async () => {
        setSaving(true);
        try {
            const parsedAdjustment = parseAdjustmentInput(ownerAdjustmentInput);
            const normalizedAdjustment = parsedAdjustment === null ? 0 : parsedAdjustment;
            if (parsedAdjustment === null) {
                setOwnerAdjustmentInput('0');
            }
            setOwnerAdjustment(normalizedAdjustment);
            await updateQuote(quote.id, {
                ownerAdjustments: { amount: normalizedAdjustment },
                ownerNotes
            });
            await loadFullQuote();
            alert('Adjustments saved!');
        } catch (error) {
            alert('Failed to save adjustments: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        if (!window.confirm('Approve this quote?')) {
            return;
        }

        try {
            const parsedAdjustment = parseAdjustmentInput(ownerAdjustmentInput);
            const normalizedAdjustment = parsedAdjustment === null ? 0 : parsedAdjustment;
            if (parsedAdjustment === null) {
                setOwnerAdjustmentInput('0');
            }
            setOwnerAdjustment(normalizedAdjustment);
            await approveQuote(quote.id, selectedPaint, { adjustment: normalizedAdjustment, notes: ownerNotes });
            alert('Quote approved!');
            onClose();
        } catch (error) {
            alert('Failed to approve quote: ' + error.message);
        }
    };

    const getAdjustedTotal = (baseTotal) => baseTotal + ownerAdjustment;
    const canEdit = quote.status === 'pending' || quote.status === 'needs_manual_review';

    if (loading) {
        return (
            <div className="quote-detail-modal">
                <div className="modal-content">
                    <p>Loading quote details...</p>
                    <button onClick={onClose}>Close</button>
                </div>
            </div>
        );
    }

    if (!quote || !quote.id) return null;

    const pricingOptions = quote.pricingOptions || [];
    const analysis = quote.claudeAnalysis || {};
    const m = typeof quote.measurements === 'string' ? JSON.parse(quote.measurements) : (quote.measurements || {});
    const c = typeof quote.colors === 'string' ? JSON.parse(quote.colors) : (quote.colors || {});
    const exteriorSqft = calculateExteriorSqft(m);
    const photoChecklist = m.photoChecklist || {};
    const checklistItems = [
        { key: 'front', label: 'Front wide shot' },
        { key: 'back', label: 'Back wide shot' },
        { key: 'left', label: 'Left wide shot' },
        { key: 'right', label: 'Right wide shot' },
        { key: 'detailDamage', label: 'Detail damage/repairs' }
    ];

    return (
        <div className="quote-detail-modal">
            <div className="modal-content">
                <button className="close-btn" onClick={onClose}>×</button>

                <h2>
                    Quote #{quote.id.substring(0, 8)}
                    <StatusBadge status={quote.status} />
                </h2>

                <section>
                    <h3>Customer Information</h3>
                    <div className="customer-info-grid">
                        <p><strong>Name:</strong> {quote.customerName}</p>
                        <p><strong>Phone:</strong> {quote.customerPhone}</p>
                        <p><strong>Email:</strong> {quote.customerEmail}</p>
                        <p><strong>Submitted by:</strong> {quote.createdBy}</p>
                    </div>
                    <p><strong>Address:</strong> {quote.customerAddress}</p>
                </section>

                <section>
                    <h3>Measurements</h3>
                    <div className="measurements-grid">
                        <div>
                            <strong>Wall Dimensions:</strong>
                            <table style={{ marginTop: '8px', fontSize: '14px' }}>
                                <tbody>
                                    <tr><td>Front:</td><td>{m.frontWall?.length}' x {m.frontWall?.height}'</td><td style={{ color: '#666' }}>= {(m.frontWall?.length || 0) * (m.frontWall?.height || 0)} sqft</td></tr>
                                    <tr><td>Left:</td><td>{m.leftWall?.length}' x {m.leftWall?.height}'</td><td style={{ color: '#666' }}>= {(m.leftWall?.length || 0) * (m.leftWall?.height || 0)} sqft</td></tr>
                                    <tr><td>Right:</td><td>{m.rightWall?.length}' x {m.rightWall?.height}'</td><td style={{ color: '#666' }}>= {(m.rightWall?.length || 0) * (m.rightWall?.height || 0)} sqft</td></tr>
                                    <tr><td>Back:</td><td>{m.backWall?.length}' x {m.backWall?.height}'</td><td style={{ color: '#666' }}>= {(m.backWall?.length || 0) * (m.backWall?.height || 0)} sqft</td></tr>
                                </tbody>
                            </table>
                            <p style={{ marginTop: '8px', fontWeight: '600' }}>Total Exterior: {exteriorSqft.toLocaleString()} sqft</p>
                            <p><strong>Stories:</strong> {m.stories}</p>
                        </div>
                        <div>
                            <strong>Additional Areas:</strong>
                            <ul style={{ marginTop: '8px' }}>
                                <li>Trim: {m.trimLinearFeet || 0} linear ft</li>
                                <li>Gutters: {m.gutterLinearFeet || 0} linear ft</li>
                                <li>
                                    Soffits: {m.soffitLinearFeet || 0} linear ft
                                    {m.soffitAvgWidthFt ? ` (avg width ${m.soffitAvgWidthFt} ft)` : ''}
                                </li>
                                <li>Fascia: {m.fasciaLinearFeet || 0} linear ft</li>
                                <li>Windows: {m.windowCount || 0}</li>
                                <li>Doors: {m.doorCount || 0}</li>
                                <li>Garage Doors: {m.garageDoorCount || 0}</li>
                                <li>Shutters: {m.shutterCount || 0}</li>
                                {(m.columnsPillarsCount || 0) > 0 && (
                                    <li>
                                        Columns/Pillars: {m.columnsPillarsCount}
                                        {m.columnsPillarsAvgSize ? ` (avg size ${m.columnsPillarsAvgSize} ft)` : ''}
                                    </li>
                                )}
                                {m.twoToneGuttersSoffits && (
                                    <li>Two-tone gutters/soffits: Yes</li>
                                )}
                                <li>Porch Ceilings: {(m.porchCeilingsFront || 0) + (m.porchCeilingsBack || 0)} sqft</li>
                                {m.poolDeckSqft > 0 && <li>Pool Deck: {m.poolDeckSqft} sqft</li>}
                            </ul>
                        </div>
                    </div>
                </section>

                <section>
                    <h3>Colors</h3>
                    <div className="colors-list">
                        <ColorSwatch color={c.mainHouse} label="Main House" />
                        <ColorSwatch color={c.trim} label="Trim" />
                        <ColorSwatch color={c.gutters} label="Gutters" />
                        <ColorSwatch color={c.soffits} label="Soffits" />
                        <ColorSwatch color={c.fascia} label="Fascia" />
                        <ColorSwatch color={c.doors} label="Doors" />
                        <ColorSwatch color={c.garageDoors} label="Garage Doors" />
                        <ColorSwatch color={c.shutters} label="Shutters" />
                        <ColorSwatch color={c.porchCeilings} label="Porch Ceilings" />
                        {c.paintQualityPreference && (
                            <p style={{ marginTop: '8px' }}>
                                <strong>Worker Preferred Tier:</strong> {formatPaintTier(c.paintQualityPreference)}
                            </p>
                        )}
                        <p style={{ marginTop: '8px' }}>
                            <strong>Color Considerations:</strong> {formatColorComplexity(c.colorComplexity)}
                        </p>
                    </div>
                </section>



                {quote.photoUrls && quote.photoUrls.length > 0 && (
                    <section>
                        <h3>Photos ({quote.photoUrls.length})</h3>
                        <div className="photo-gallery">
                            {quote.photoUrls.map((url, index) => (
                                <img
                                    key={index}
                                    src={url}
                                    alt={`Photo ${index + 1}`}
                                    onClick={() => setSelectedPhoto(url)}
                                    style={{ cursor: 'pointer' }}
                                />
                            ))}
                        </div>
                        {selectedPhoto && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.9)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2000,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedPhoto(null)}
                            >
                                <img src={selectedPhoto} alt="Full size" style={{ maxWidth: '90%', maxHeight: '90%' }} />
                            </div>
                        )}
                    </section>
                )}

                {(analysis.stuccoCondition || analysis.trimCondition || analysis.moldSeverity || analysis.prepWorkIntensity) && (
                    <section>
                        <h3>AI Analysis</h3>
                        {analysis.prepWorkIntensity && (
                            <p style={{ marginBottom: '12px' }}>
                                <strong>Prep Work Intensity:</strong>{' '}
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: analysis.prepWorkIntensity === 'extensive' ? '#fee2e2' :
                                        analysis.prepWorkIntensity === 'moderate' ? '#fef3c7' : '#d1fae5',
                                    color: analysis.prepWorkIntensity === 'extensive' ? '#991b1b' :
                                        analysis.prepWorkIntensity === 'moderate' ? '#92400e' : '#065f46'
                                }}>
                                    {analysis.prepWorkIntensity.charAt(0).toUpperCase() + analysis.prepWorkIntensity.slice(1)}
                                </span>
                            </p>
                        )}
                        <div className="analysis-grid">
                            {analysis.stuccoCondition && (
                                <div className="analysis-item">
                                    <strong>Stucco:</strong> {analysis.stuccoCondition.rating}/5
                                    {analysis.stuccoCondition.issue && (
                                        <span style={{ color: '#6b7280', marginLeft: '8px' }}>({analysis.stuccoCondition.issue.replace(/_/g, ' ')})</span>
                                    )}
                                    {analysis.stuccoCondition.confidence && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '6px' }}>[{analysis.stuccoCondition.confidence}]</span>
                                    )}
                                    <br />
                                    <em style={{ fontSize: '13px', color: '#666' }}>{analysis.stuccoCondition.reasoning}</em>
                                </div>
                            )}
                            {analysis.trimCondition && (
                                <div className="analysis-item">
                                    <strong>Trim:</strong> {analysis.trimCondition.rating}/5
                                    {analysis.trimCondition.issue && (
                                        <span style={{ color: '#6b7280', marginLeft: '8px' }}>({analysis.trimCondition.issue})</span>
                                    )}
                                    {analysis.trimCondition.confidence && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '6px' }}>[{analysis.trimCondition.confidence}]</span>
                                    )}
                                    <br />
                                    <em style={{ fontSize: '13px', color: '#666' }}>{analysis.trimCondition.reasoning}</em>
                                </div>
                            )}
                            {analysis.moldSeverity && (
                                <div className="analysis-item">
                                    <strong>Mold:</strong> {analysis.moldSeverity.level}
                                    <br />
                                    <em style={{ fontSize: '13px', color: '#666' }}>{analysis.moldSeverity.reasoning}</em>
                                </div>
                            )}
                            {analysis.rustPeeling && (
                                <div className="analysis-item">
                                    <strong>Rust/Peeling:</strong> {analysis.rustPeeling.severity}
                                    <br />
                                    <em style={{ fontSize: '13px', color: '#666' }}>{analysis.rustPeeling.reasoning}</em>
                                </div>
                            )}
                        </div>

                        {analysis.detectedIssues && analysis.detectedIssues.length > 0 && (
                            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px' }}>
                                <strong>Detected Issues:</strong>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    {analysis.detectedIssues.map((issue, i) => (
                                        <li key={i}>
                                            {issue.type.replace(/_/g, ' ')}
                                            {issue.confidence && <span style={{ fontSize: '11px', color: '#9ca3af' }}> [{issue.confidence}]</span>}
                                            {issue.reasoning && <span style={{ fontSize: '12px', color: '#666' }}> - {issue.reasoning}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {analysis.flaggedConcerns && analysis.flaggedConcerns.length > 0 && (
                            <div className="flagged-concerns" style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '6px' }}>
                                <strong>Flagged Concerns:</strong>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    {analysis.flaggedConcerns.map((concern, i) => (
                                        <li key={i}>{concern}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>
                )}

                <section>
                    <h3>Pricing Options</h3>
                    <div className="pricing-options">
                        {pricingOptions.map((option) => (
                            <div
                                key={option.paintQuality}
                                className={`pricing-card ${selectedPaint === option.paintQuality ? 'selected' : ''}`}
                                onClick={() => setSelectedPaint(option.paintQuality)}
                            >
                                <h4>{option.paintName}</h4>
                                <div className="price">
                                    ${getAdjustedTotal(option.total).toLocaleString()}
                                    {ownerAdjustment !== 0 && (
                                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                                            (was ${option.total.toLocaleString()})
                                        </span>
                                    )}
                                </div>
                                <div className="breakdown">
                                    <p>Base: ${(option.basePrice || 0).toLocaleString()}</p>
                                    <p>Condition Add-ons: ${(option.conditionAdjustments || 0).toLocaleString()}</p>
                                    {ownerAdjustment !== 0 && (
                                        <p style={{ color: ownerAdjustment > 0 ? '#b45309' : '#065f46' }}>
                                            Owner Adjustment: {ownerAdjustment > 0 ? '+' : ''}{ownerAdjustment.toLocaleString()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {canEdit && (
                    <section className="adjustment-section">
                        <h3>Owner Adjustments</h3>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            <label>
                                <span style={{ fontWeight: '500' }}>Price Adjustment ($):</span>
                                <input
                                    type="text"
                                    inputMode="text"
                                    pattern="^-?\\d*(\\.\\d+)?$"
                                    value={ownerAdjustmentInput}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setOwnerAdjustmentInput(nextValue);
                                        const parsed = parseAdjustmentInput(nextValue);
                                        if (parsed !== null) {
                                            setOwnerAdjustment(parsed);
                                        }
                                    }}
                                    onBlur={() => {
                                        const parsed = parseAdjustmentInput(ownerAdjustmentInput);
                                        if (parsed === null) {
                                            setOwnerAdjustment(0);
                                            setOwnerAdjustmentInput('0');
                                        } else {
                                            setOwnerAdjustment(parsed);
                                            setOwnerAdjustmentInput(String(parsed));
                                        }
                                    }}
                                    placeholder="Enter + or - amount"
                                    style={{
                                        marginLeft: '8px',
                                        padding: '6px 10px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        width: '120px'
                                    }}
                                />
                                <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: '8px' }}>
                                    (positive = add, negative = discount)
                                </span>
                            </label>
                            <label>
                                <span style={{ fontWeight: '500' }}>Notes / Rationale:</span>
                                <textarea
                                    value={ownerNotes}
                                    onChange={(e) => setOwnerNotes(e.target.value)}
                                    placeholder="Reason for adjustment (optional)"
                                    rows={2}
                                    style={{
                                        display: 'block',
                                        marginTop: '4px',
                                        padding: '8px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        width: '100%',
                                        resize: 'vertical'
                                    }}
                                />
                            </label>
                            <button
                                onClick={handleSaveAdjustments}
                                disabled={saving}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    opacity: saving ? 0.6 : 1,
                                    width: 'fit-content'
                                }}
                            >
                                {saving ? 'Saving...' : 'Save Adjustments'}
                            </button>
                        </div>
                    </section>
                )}

                <div className="actions">
                    <button onClick={handleApprove} className="primary">
                        Approve Quote
                    </button>
                    <button onClick={onClose} className="secondary">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
